"use client";
import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useJuneSoEventTracker } from "@stadium/services/june-analytics";
import { FeatureFlagWrapper, useFlags } from "@stadium/services/launch-darkly";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import useStadiumSharedGlobalConfigContext from "@stadium/shared/src/hooks/use-global-context";

import BootstrapDropdown, {
  DropdownItem as BootstrapDropdownItem,
} from "@stadium/ui/src/components/BootstrapDropdown";
import ProfileDdBody from "@stadium/ui/src/components/ProfileDropdown/body";
import ProfileList from "@stadium/ui/src/components/ProfileDropdown/list";
import Button, { LinkButton } from "@stadium/shared/src/components/shared/button";
import HeaderLink from "@stadium/shared/src/components/shared/link-tag";
import UserRewardPoints from "@stadium/shared/src/components/header/shared/user-reward-points";
import Avatar from "@stadium/shared/src/components/header/avatar";
import CreateWorkspaceModal from "@stadium/shared/src/components/header/profile-dropdown/create-workspace-modal";
import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";
import HelpCenterLink from "@stadium/shared/src/components/header/shared/help-center-link";

import {
  getMiniAccountDropdownData,
  getNavAccountDropdownData,
  getProfileData,
} from "@stadium/shared/src/helpers/accounts";
import { stopEventPropagation } from "@stadium/helpers/resource";
import { isStadiumSite } from "@stadium/shared/src/helpers/brands";

import {
  ACCOUNT_DROPDOWN,
  ACCOUNT_DROPDOWN_CTA_SERVICES,
} from "@stadium/shared/src/constants/june-so";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";
import {
  ANALYTICS_EVENT_ACTION_OPENED,
  DEFAULT_EVENT_LOCATION_ANALYTICS,
} from "@stadium/services/analytics/constants/default";
import {
  STORES,
  SNACKMAGIC,
  SUPERIORDISHES,
  SWAGMAGIC,
} from "@stadium/shared/src/constants/brands";
import {
  createWorkspaceNudgeFlag,
  requestToJoinWorkspaceFlag,
  workspaceContactsFlag,
  workspaceSidebarFlag,
  workspaceWalletFlag,
  hideCreateNewWorkspaceFlag,
} from "@stadium/services/launch-darkly/constants";
import { ACCOUNT_ADVANCED_OPTIONS_URL } from "constants/routes";
import { getPathnameFromUrl } from "@stadium/helpers/routes";
import useCreateWorkspaceNudge from "@stadium/shared/src/components/header/profile-dropdown/use-create-workspace-nudge";

import defaultStyles from "./main.module.scss";

const emptyObj = {};
const defaultFn = () => {};

const ProfileWeb = ({
  logout,
  profileData,
  isMinifiedAccountDropdown = false,
  showHeaderNavLinks = false,
  accountDropdownKey,
  accessibilityEnabled = true,
  styles = defaultStyles,
  isMarketingPage = false,
  isNewSwagmagicHost = false,
}) => {
  const {
    [workspaceSidebarFlag]: workspaceSidebarFlagEnabled,
    [workspaceWalletFlag]: workspaceWalletFlagEnabled,
    [workspaceContactsFlag]: workspaceContactsEnabled,
    [requestToJoinWorkspaceFlag]: requestToJoinWorkspaceFlagEnabled,
    [createWorkspaceNudgeFlag]: createWorkspaceNudgeFlagEnabled,
  } = useFlags();
  const { site } = useStadiumSharedGlobalConfigContext();

  const byStadiumSite = isStadiumSite(site);

  const { trackEvent } = useJuneSoEventTracker({
    services: ACCOUNT_DROPDOWN_CTA_SERVICES,
    elementPosition: ACCOUNT_DROPDOWN,
  });

  const withAnalytics = useWithAnalytics();

  const onProfileMenuToggle = useCallback(
    (isOpen) => {
      if (isOpen) {
        withAnalytics(undefined, {
          track: true,
          target: HEADER_ANALYTICS_TARGET.PROFILE_MENU,
          action: ANALYTICS_EVENT_ACTION_OPENED,
        })();
      }
    },
    [withAnalytics],
  );

  const defaultProfileDataLinks = useMemo(() => getProfileData({ trackEvent }), [trackEvent]);

  const miniProfileDataLinks = useMemo(
    () => getMiniAccountDropdownData({ trackEvent, workspaceWalletFlagEnabled }),
    [trackEvent],
  );

  const {
    firstName,
    email,
    rewardPoints,
    headLabel,
    vipBadge = null,
    workspacesByDomainExists,
    fullName,
    activeWorkspaceSlug,
    workspaces,
    onWorkspaceSelection = null,
    canViewShops,
  } = profileData || emptyObj;
  const { closeCreateWorkspaceModal, handleCreateWorkspaceClick, showCreateWorkspaceModal } =
    useCreateWorkspaceNudge({
      isNudgeEnabled: createWorkspaceNudgeFlagEnabled,
      workspaces,
    });

  const navDataLinks = useMemo(
    () =>
      getNavAccountDropdownData({
        trackEvent,
        isStore: site === STORES,
        showHeaderNavLinks,
        accessibilityEnabled,
        workspaceSlug: activeWorkspaceSlug,
        workspaceSidebarFlagEnabled,
        workspaceContactsEnabled,
        canViewShops,
      }),
    [showHeaderNavLinks, activeWorkspaceSlug, workspaceSidebarFlagEnabled],
  );

  const activeWorkspace = useMemo(
    () => workspaces?.find(({ slug }) => slug === activeWorkspaceSlug),
    [workspaces, activeWorkspaceSlug],
  );

  const firstNameToShow = firstName?.split(" ")[0];
  const { name: workspaceName } = activeWorkspace || emptyObj;

  const getWorkspaceSelectionRoute = useCallback(
    ({ selectedWorkspaceSlug, currentPath }) => {
      if (onWorkspaceSelection) {
        return onWorkspaceSelection(selectedWorkspaceSlug, currentPath);
      }
      return currentPath.replace(activeWorkspaceSlug, selectedWorkspaceSlug);
    },
    [activeWorkspaceSlug, onWorkspaceSelection],
  );

  const renderWorkspace = ({ name, iconUrl, isActive, isDefault }) => (
    <>
      <div className={styles.nameBlock}>
        {iconUrl ? (
          <Multimedia
            type="image"
            src={iconUrl}
            alt={name}
            className={styles.companyLogo}
            isNextImage={false}
          />
        ) : (
          <span className={styles.iconDefault} />
        )}
        <span className={`${styles.companyName} ${isActive ? styles.active : ""}`}>{name}</span>
        {isDefault && <span className={styles.defaultText}>Default</span>}
      </div>
      {isActive && <span className={styles.iconTick} />}
    </>
  );

  const workspaceList = (
    <>
      <ul className={styles.workSpaceSection}>
        {workspaces?.map((workspace) => {
          const { id, slug } = workspace;
          const isActive = activeWorkspace?.id === id;

          return site === STORES ? (
            <li className={styles.listItem} key={id}>
              <Button
                styleType="link"
                onClick={() => {
                  onWorkspaceSelection?.(slug, getPathnameFromUrl(window.location.pathname));
                }}
                className={styles.link}
                tabIndex={0}
                title={workspace.name}
              >
                {renderWorkspace({ ...workspace, isActive })}
              </Button>
            </li>
          ) : (
            <li className={styles.listItem} key={id}>
              <LinkButton
                styleType="no-style"
                href={getWorkspaceSelectionRoute({
                  selectedWorkspaceSlug: slug,
                  currentPath: getPathnameFromUrl(window.location.pathname),
                })}
                className={styles.link}
                tabIndex={0}
                title={workspace.name}
              >
                {renderWorkspace({ ...workspace, isActive })}
              </LinkButton>
            </li>
          );
        })}
        <FeatureFlagWrapper
          flag={hideCreateNewWorkspaceFlag}
          fallback={
            <li className={styles.listItem}>
              <Button
                styleType="no-style"
                onClick={handleCreateWorkspaceClick}
                className={`${styles.link} ${styles.newWorkSpaceLink}`}
                title="Create New Workspace"
              >
                <span className={styles.iconAdd} />
                <span className={styles.text}>Create New Workspace</span>
              </Button>
            </li>
          }
        ></FeatureFlagWrapper>
      </ul>

      {requestToJoinWorkspaceFlagEnabled && workspacesByDomainExists && (
        <div className={styles.workspaceBanner}>
          Looks like your team’s already on <br className={styles.break} />
          Stadium!{" "}
          <LinkButton
            styleType="no-style"
            href={ACCOUNT_ADVANCED_OPTIONS_URL}
            className={styles.link}
            title="View Workspaces to join"
          >
            View Workspaces to join
          </LinkButton>
        </div>
      )}
    </>
  );

  const renderLink = ({
    to,
    linkClassName,
    label,
    onLinkClick = null,
    renderPillTag = defaultFn,
    otherProps = {},
  }) => (
    <HeaderLink
      url={to}
      urlText={label}
      className={linkClassName}
      onClick={onLinkClick}
      renderPillTag={renderPillTag}
      target={!byStadiumSite ? "_blank" : ""}
      eventLocationAnalytics={DEFAULT_EVENT_LOCATION_ANALYTICS}
      {...otherProps}
    />
  );

  const renderHead = () => (
    <div className={`${styles.accountContainerInner} ${styles.accountContainerInnerFlex}`}>
      <div className={styles.avtarArea}>
        <Avatar
          user={profileData}
          workspace={activeWorkspace}
          className={isNewSwagmagicHost ? styles.avatarContainerSW : ""}
        />
      </div>
      <div className={styles.accountInfo}>
        <div className={styles.accountName}>{firstNameToShow}</div>
        {workspaceSidebarFlagEnabled && workspaceName && (
          <div className={styles.companyName}>{workspaceName}</div>
        )}
        {typeof rewardPoints === "number" && (
          <UserRewardPoints points={rewardPoints} site={site} onClick={stopEventPropagation} />
        )}
      </div>
      {vipBadge}
    </div>
  );

  const profileDataLinks = useMemo(() => {
    if (byStadiumSite) {
      return navDataLinks;
    }
    return isMinifiedAccountDropdown ? miniProfileDataLinks : defaultProfileDataLinks;
  }, [isMinifiedAccountDropdown, byStadiumSite, navDataLinks]);

  const programList = profileDataLinks.map((data) => (
    <ProfileList
      {...data}
      renderLink={renderLink}
      key={`item-${data?.title}`}
      styles={styles}
      className={data?.className}
    />
  ));

  const chevronOpenDDStyle = (() => {
    switch (site) {
      case SNACKMAGIC:
        return styles.snackmagicOpenDDStyle;
      case SUPERIORDISHES:
        return styles.superdishesOpenDDStyle;
      case SWAGMAGIC:
        return styles.swagmagicOpenDDStyle;
      default:
        return styles.defaultBgOpenDDStyle;
    }
  })();

  const label = isNewSwagmagicHost ? (
    <Avatar user={profileData} workspace={activeWorkspace} className={styles.avatarContainerSW} />
  ) : (
    <>
      {headLabel ?? renderHead()}
      <Button
        className={`${styles.profileDDIconContainer} ${chevronOpenDDStyle}`}
        data-testid="profile-dropdown-icon"
        title="Account Dropdown"
      >
        <i className={styles.profileDDIcon} />
      </Button>
    </>
  );

  const handleWebLogout = () => {
    trackEvent({ target: "logout" });
    logout();
  };

  const list = useMemo(
    () => [
      ...programList,
      ...(workspaceSidebarFlagEnabled && byStadiumSite ? [workspaceList] : []),
    ],
    [programList, workspaceSidebarFlagEnabled, activeWorkspace],
  );

  return (
    <>
      {showCreateWorkspaceModal && <CreateWorkspaceModal closeModal={closeCreateWorkspaceModal} />}
      <div
        className={classNames(styles.userDDContainer, styles.accountNavDropdown, {
          [styles.profileBorder]: site === STORES,
          [styles.accountNavMarketing]: isMarketingPage,
          [styles.swagmagicUserDDContainer]: isNewSwagmagicHost,
        })}
      >
        <BootstrapDropdown
          label={label}
          styles={styles}
          menuClassName={`${styles.dropdownWrapper} ${
            isNewSwagmagicHost ? styles.swagmagicDropdownWrapper : ""
          }`}
          key={accountDropdownKey}
          ariaLabel="Profile Dropdown"
          onToggle={onProfileMenuToggle}
        >
          <BootstrapDropdownItem>
            <ProfileDdBody
              styles={styles}
              programList={list}
              email={email}
              userName={workspaceSidebarFlagEnabled ? fullName : firstNameToShow}
              helpCenterCta={
                <HelpCenterLink eventLocationAnalytics={DEFAULT_EVENT_LOCATION_ANALYTICS} />
              }
              logoutButton={
                <Button
                  className={styles.logoutButton}
                  onClick={handleWebLogout}
                  styleType="link"
                  title="logout"
                  eventLocationAnalytics={{
                    track: true,
                    target: HEADER_ANALYTICS_TARGET.LOGOUT,
                  }}
                >
                  LOGOUT
                </Button>
              }
            />
          </BootstrapDropdownItem>
        </BootstrapDropdown>
      </div>
    </>
  );
};

export default ProfileWeb;
